/**
 * Search Drawer — Web Components
 * ================================
 * Custom elements for the search drawer feature:
 *   - <predictive-search>   — fetches /search/suggest and renders results
 *   - <search-form>         — wraps the search form, manages recent searches in localStorage
 *   - <recent-searches>     — renders recent search chips from localStorage
 *   - <trending-searches>   — renders trending search pills from inline JSON
 *
 * Dependencies: None (vanilla JS, no external libs)
 * localStorage keys:
 *   "recentSearches" — array of { query, uri } (max 5)
 */

/* ── PredictiveSearch ───────────────────────────────────────────────────────── */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    const debounce = (fn, wait) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    };

    this.#addSpinner();

    this.type = this.getAttribute('type');
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('predictive-search-results');
    this.additionalContent = this.parentElement.querySelector('.js-search-content');
    this.clearBtn = this.querySelector('[data-sd-clear]');
    this.searchTerm = '';
    this.abortController = null;

    this.#updateLoading();

    this.input.addEventListener('input', debounce(this.onChange.bind(this), 250));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Clear button
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.input.focus();
        this.close();
        this.#updateQueryState();
      });
    }

    // Save search term to recent searches when user clicks a result link or suggestion
    this.results.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      const searchForm = link.closest('search-form');
      if (!searchForm) return;

      // For suggestion links (go to /search?q=...), save the suggestion's query text
      const searchUrl = this.getAttribute('search-url');
      const [linkPath] = link.getAttribute('href').split('?');
      if (searchUrl && searchUrl === linkPath) {
        const url = new URL(link.href, location.origin);
        const q = url.searchParams.get('q');
        if (q) searchForm.addRecentSearch(q, link.href);
        return;
      }

      // For product links, save the current form query
      searchForm.addFormStateToRecentSearches();
    });

    if (this.type === 'sidebar') {
      window.visualViewport.addEventListener(
        'resize',
        debounce(this.#setDrawerContentMaxHeight.bind(this), 50)
      );
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  #updateQueryState() {
    const drawer = this.closest('.sd-drawer');
    if (!drawer) return;
    if (this.getQuery().length > 0) {
      drawer.classList.add('has-query');
    } else {
      drawer.classList.remove('has-query');
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onChange() {
    if (this.abortController) this.abortController.abort();

    const searchTerm = this.getQuery();
    this.searchTerm = searchTerm;
    this.#updateQueryState();

    // One character is too little to suggest on: skip the request and keep the default content
    if (searchTerm.length < 2) {
      this.close();
      this.#updateLoading();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    this.abortController = new AbortController();
    this.#updateLoading(true);

    fetch(`/search/suggest?q=${encodeURIComponent(searchTerm)}&resources[type]=product,query&resources[limit]=8&section_id=search-drawer-predictive`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-search-drawer-predictive').innerHTML;

        this.results.innerHTML = resultsMarkup;
        this.open();
        this.#updateLoading();
        this.input.setAttribute('aria-activedescendant', '');
        if (typeof initPredictiveScrollIndicators === 'function') initPredictiveScrollIndicators();
        this.#initNoResultsScrollIndicators();
        this.#bindSearchAgain();
      })
      .catch((error) => {
        if (error.name && error.name === 'AbortError') return;
        this.#updateLoading();
        this.close();
        throw error;
      })
      .finally(() => {
        this.abortController = null;
      });
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', 'true');
    this.spinner.setAttribute('hidden', 'true');
    this.results.removeAttribute('hidden');
    if (this.additionalContent) this.additionalContent.setAttribute('hidden', true);
  }

  close() {
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.spinner.setAttribute('hidden', 'true');
    this.results.setAttribute('hidden', 'true');
    if (this.additionalContent) this.additionalContent.removeAttribute('hidden');
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll('predictive-search-results li, button.sd-search-all__btn')
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1
        ? 0
        : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0
        ? allVisibleElements.length - 1
        : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  #addSpinner() {
    const spinner = document.createElement('div');
    spinner.classList.add('sd-loader');
    spinner.setAttribute('hidden', true);
    spinner.innerHTML = '<div class="sd-spinner"></div>';
    this.spinner = spinner;
    this.querySelector('.sd-input-limiter').append(spinner);
  }

  #updateLoading(isLoading = false) {
    const drawer = this.closest('.sd-drawer');
    if (isLoading) {
      this.classList.add('is-loading');
      if (drawer) drawer.classList.add('is-loading');
      if (this.spinner) this.spinner.removeAttribute('hidden');
    } else {
      this.classList.remove('is-loading');
      if (drawer) drawer.classList.remove('is-loading');
      if (this.spinner) this.spinner.setAttribute('hidden', true);
    }
  }

  #bindSearchAgain() {
    const btn = this.results.querySelector('[data-search-again]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      this.input.value = '';
      this.input.focus();
      this.close();
      this.#updateQueryState();
    });
  }

  #initNoResultsScrollIndicators() {
    this.results.querySelectorAll('.sd-no-results__bestsellers').forEach((section) => {
      initScrollIndicator(section, '.sd-products-scroll');
    });
  }

  #setDrawerContentMaxHeight() {
    const drawer = this.closest('.sd-drawer');
    if (drawer) {
      const inputBar = drawer.querySelector('.sd-input-wrapper');
      if (window.visualViewport.height < window.innerHeight) {
        drawer.style.setProperty(
          '--sd-max-content-height',
          `${window.visualViewport.height - (inputBar ? inputBar.offsetHeight : 0)}px`
        );
      } else {
        drawer.style.removeProperty('--sd-max-content-height');
      }
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);


/* ── SearchForm ─────────────────────────────────────────────────────────────── */
class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
  }

  connectedCallback() {
    if (this.hasAttribute('add-recent-search-on-load')) {
      this.addFormStateToRecentSearches();
    }

    // Save query to recent searches on form submit (Enter key or "Search for X" button)
    if (this.form) {
      this.form.addEventListener('submit', () => {
        this.addFormStateToRecentSearches();
      });
    }
  }

  addRecentSearch(query, uri) {
    if (!query || !uri) return;

    const getUniqueListBy = (arr, key) =>
      [...new Map(arr.map(item => [item[key], item])).values()];

    let recentSearches = [{ query, uri }];
    const stored = localStorage.getItem('recentSearches');

    if (stored) {
      const prev = JSON.parse(stored);
      const combined = [...prev, ...recentSearches];
      recentSearches = getUniqueListBy(combined.reverse(), 'query').reverse();
      if (recentSearches.length > 5) {
        recentSearches = recentSearches.slice(-5);
      }
    }

    localStorage.setItem('recentSearches', JSON.stringify(recentSearches));
  }

  addFormStateToRecentSearches() {
    if (!this.form) return;

    const form = new FormData(this.form);
    const search = new URLSearchParams(form);
    const formAction = new URL(`${location.origin}${this.form.getAttribute('action')}`);
    const combinedSearch = new URLSearchParams({
      ...Object.fromEntries(formAction.searchParams),
      ...Object.fromEntries(search)
    });

    this.addRecentSearch(form.get('q'), `${location.origin}${formAction.pathname}?${combinedSearch.toString()}`);
  }
}
customElements.define('search-form', SearchForm);


/* ── RecentSearches ─────────────────────────────────────────────────────────── */
class RecentSearches extends HTMLElement {
  constructor() {
    super();
    this.update();
    this.addEventListener('click', this.onClick.bind(this));
  }

  static toTitleCase(str) {
    return str.replace(/\b\w/g, c => c.toUpperCase());
  }

  update() {
    const stored = localStorage.getItem('recentSearches');
    const ul = this.querySelector('ul');
    let items = [];

    if (stored) {
      try { items = JSON.parse(stored); } catch (e) { /* invalid JSON */ }
    }

    if (items.length > 0) {
      this.parentElement.classList.add('has-recent-searches');
      const searchIcon = '<svg class="sd-chip__icon" width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.5" stroke="currentColor" stroke-width="1.5"/><line x1="10.35" y1="10.35" x2="13.5" y2="13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      ul.innerHTML = items.reverse().reduce((html, item) => {
        const display = RecentSearches.toTitleCase(item.query);
        return `${html}
          <li class="sd-chip-item">
            <a href="${item.uri}" class="sd-chip">
              ${searchIcon}
              <span class="sd-chip-text">${display}</span>
            </a>
            <button type="button" class="sd-chip-remove" data-query="${item.query}" aria-label="Remove ${display}">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><circle cx="6" cy="6" r="6"/><path d="M4.17 4.17L7.83 7.83M7.83 4.17L4.17 7.83" stroke="white" stroke-width="1.2" stroke-linecap="round"/></svg>
            </button>
          </li>`;
      }, '');
      this.removeAttribute('hidden');
    } else {
      if (ul) ul.innerHTML = '';
      this.setAttribute('hidden', '');
      this.parentElement.classList.remove('has-recent-searches');
    }
  }

  onClick(e) {
    // Handle "Clear All"
    const clearAll = e.target.closest('[data-clear-all]');
    if (clearAll) {
      e.preventDefault();
      localStorage.removeItem('recentSearches');
      this.update();
      document.querySelectorAll('recent-searches').forEach((el) => {
        if (el !== this) el.update();
      });
      return;
    }

    // Handle individual chip remove
    const removeBtn = e.target.closest('.sd-chip-remove');
    if (!removeBtn) return;

    e.preventDefault();
    e.stopPropagation();

    const query = removeBtn.dataset.query;
    const stored = localStorage.getItem('recentSearches');
    if (stored) {
      const searches = JSON.parse(stored).filter(item => item.query !== query);
      localStorage.setItem('recentSearches', JSON.stringify(searches));
    }
    this.update();
    document.querySelectorAll('recent-searches').forEach((el) => {
      if (el !== this) el.update();
    });
  }
}
customElements.define('recent-searches', RecentSearches);


/* ── TrendingSearches ───────────────────────────────────────────────────────── */
class TrendingSearches extends HTMLElement {
  constructor() {
    super();

    const script = this.querySelector('script[type="application/json"]');
    if (!script) return;

    try {
      const items = JSON.parse(script.textContent);
      if (!Array.isArray(items) || items.length === 0) return;

      const ul = this.querySelector('ul');
      const searchUrl = this.getAttribute('data-search-url') || '/search';
      const trendIcon = '<svg class="sd-trending-icon" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M2.5 18.5L9.5 11.5L13.5 15.5L22 6.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 6.5H22V12.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      ul.innerHTML = items.map(item => `
        <li>
          <a href="${searchUrl}?q=${encodeURIComponent(item.query)}" class="sd-trending-pill">
            ${trendIcon}
            ${item.label}
          </a>
        </li>
      `).join('');

      // Save trending search to recent searches before navigation
      ul.addEventListener('click', (e) => {
        const link = e.target.closest('a');
        if (!link) return;
        const url = new URL(link.href, location.origin);
        const q = url.searchParams.get('q');
        if (!q) return;
        const searchForm = this.closest('search-form');
        if (searchForm) searchForm.addRecentSearch(q, link.href);
      });
    } catch (e) {
      // Invalid JSON — silently fail
    }
  }
}
customElements.define('trending-searches', TrendingSearches);


/* ── Scroll indicator ───────────────────────────────────────────────────────── */
function initScrollIndicator(container, scrollerSelector) {
  const scroller = container.querySelector(scrollerSelector);
  const thumb = container.querySelector('.sd-scroll-indicator__thumb');
  if (!scroller || !thumb) return;

  function updateThumb() {
    const scrollWidth = scroller.scrollWidth;
    const clientWidth = scroller.clientWidth;
    if (scrollWidth <= clientWidth) {
      thumb.style.width = '100%';
      thumb.style.transform = 'translateX(0)';
      return;
    }
    const thumbRatio = clientWidth / scrollWidth;
    const scrollRatio = scroller.scrollLeft / (scrollWidth - clientWidth);
    const trackWidth = thumb.parentElement.clientWidth;
    const thumbWidth = Math.max(thumbRatio * trackWidth, 30);
    const maxTranslate = trackWidth - thumbWidth;

    thumb.style.width = thumbWidth + 'px';
    thumb.style.transform = 'translateX(' + (scrollRatio * maxTranslate) + 'px)';
  }

  scroller.addEventListener('scroll', updateThumb, { passive: true });
  updateThumb();
}

/* Bestsellers strip — not rendered with the page. sections/search-drawer.liquid
   leaves a skeleton in [data-sd-bestsellers-slot]; swap in
   sections/search-drawer-bestsellers.liquid the first time the drawer opens,
   or earlier when a search trigger is hovered/focused. */
let bestsellersRequest = null;

function loadBestsellers() {
  const slot = document.querySelector('[data-sd-bestsellers-slot]');
  if (!slot || bestsellersRequest) return;

  const url = new URL(window.location.href);
  url.searchParams.set('section_id', 'search-drawer-bestsellers');

  // Pass a string: CleverTap's fetch wrapper (/a/s/shopifyEvents.js) calls
  // .includes() on the input and rejects the promise for a URL object.
  bestsellersRequest = fetch(url.toString())
    .then((response) => {
      if (!response.ok) throw new Error(`Bestsellers strip: HTTP ${response.status}`);
      return response.text();
    })
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.getElementById('shopify-section-search-drawer-bestsellers');
      if (!content) return;

      slot.replaceChildren(...content.childNodes);
      initScrollIndicator(slot, '.sd-products-scroll');
      // Paint saved-state hearts on the new cards (assets/wishlist-controller_v2.js).
      document.dispatchEvent(new CustomEvent('wishlist:v2:refresh'));
    })
    .catch(() => {
      bestsellersRequest = null; // let the next open retry
    });
}

document.addEventListener('search-drawer:open', loadBestsellers);

function prefetchBestsellers(event) {
  if (event.target instanceof Element && event.target.closest('[onclick*="__searchDrawer"]')) loadBestsellers();
}
document.addEventListener('pointerover', prefetchBestsellers, { passive: true });
document.addEventListener('focusin', prefetchBestsellers);

/* Predictive matching products — reinit after AJAX load */
function initPredictiveScrollIndicators() {
  document.querySelectorAll('.sd-predictive__products').forEach((section) => {
    initScrollIndicator(section, '.sd-product-grid');
  });
}
